import React, { useState } from 'react';
import { CustomLink, SubTitle, Body, Col, Title, styled, Row, Quantity, Button } from '@teko/ds-desktop';
import { AddOutlined } from '@teko/ds-icon';
import { useDevice } from '../../contexts/DeviceContext.es.js';
import { formatCurrency } from '../../helpers/number.es.js';
import { useQuantity } from '../../hooks/useQuantity.es.js';
import { EnhancedImage } from '../EnhancedImage/EnhancedImage.es.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const ProductListItem = ({
  sku,
  imageUrl,
  name,
  stockQuantity,
  price,
  addToCart,
  ctaButton,
  contactButton,
  ctaTracking,
  tracking,
  link,
  unit,
  minStock,
  maxStock,
  currentStock,
  precisionToDisplayDecimalQuantity: precision
}) => {
  const { isMobile } = useDevice();
  const lastestPrice = price ? formatCurrency(price.latestPrice) : "";
  const {
    quantity,
    setQuantity,
    onDecrease,
    onIncrease,
    handleUpdateQuantity
  } = useQuantity({
    addToCart,
    precision,
    baseQuantity: 1
  });
  const [quantityDesktop, setQuantityDesktop] = useState(0);
  const formattedTotalPrice = price ? formatCurrency(price.latestPrice * (isMobile ? quantity : quantityDesktop)) : "";
  const handleAddToCartDesktop = async () => {
    try {
      await (addToCart == null ? void 0 : addToCart.onAddToCart(quantityDesktop));
      setQuantityDesktop(0);
    } catch (e) {
      return Promise.reject(e);
    }
  };
  const productInfo = /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(CustomLink, __spreadValues({}, link), /* @__PURE__ */ React.createElement(SubTitle, __spreadValues({
    level: 2,
    line: 1
  }, tracking), name)), /* @__PURE__ */ React.createElement(SkuWrapper, null, /* @__PURE__ */ React.createElement(Body, {
    level: 1,
    color: "textSecondary"
  }, "SKU: ", sku), /* @__PURE__ */ React.createElement(Divider, null), unit && /* @__PURE__ */ React.createElement(Body, {
    level: 1,
    color: "textSecondary"
  }, "\u0110\u01A1n v\u1ECB: ", unit)), !!(currentStock || currentStock === 0) && /* @__PURE__ */ React.createElement(Body, {
    style: { display: "flex" },
    level: 1,
    color: "textSecondary"
  }, "T\u1ED3n kho hi\u1EC7n t\u1EA1i:", " ", /* @__PURE__ */ React.createElement(Body, {
    marginLeft: "s4",
    level: 1,
    color: "success600"
  }, currentStock)), !!(minStock || minStock === 0) && /* @__PURE__ */ React.createElement(Body, {
    level: 1,
    color: "textSecondary"
  }, "T\u1ED3n kho nh\u1ECF nh\u1EA5t: ", minStock), !!(maxStock || maxStock === 0) && /* @__PURE__ */ React.createElement(Body, {
    level: 1,
    color: "textSecondary"
  }, "T\u1ED3n kho l\u1EDBn nh\u1EA5t: ", maxStock));
  const renderQuantity = () => {
    if (isMobile)
      return /* @__PURE__ */ React.createElement(Quantity, {
        block: true,
        disabled: !!contactButton,
        quantity,
        setQuantity,
        onMinus: onDecrease,
        onPlus: onIncrease,
        min: 0,
        max: Math.min(stockQuantity, 999),
        color: "primary500",
        size: "s",
        onBlur: handleUpdateQuantity,
        onKeyPress: (e) => {
          if (e.key === "Enter")
            handleUpdateQuantity();
        },
        decimalSeparator: ".",
        precision
      });
    return /* @__PURE__ */ React.createElement(Quantity, {
      disabled: !!contactButton,
      quantity: quantityDesktop,
      setQuantity: setQuantityDesktop,
      onMinus: () => setQuantityDesktop((prev) => prev - 1),
      onPlus: () => setQuantityDesktop((prev) => prev + 1),
      min: 0,
      max: Math.min(stockQuantity, 999),
      size: "s",
      decimalSeparator: ".",
      precision
    });
  };
  const renderImage = (size) => {
    return /* @__PURE__ */ React.createElement(ProductImage, {
      hover: "zoom",
      objectFit: "contain",
      width: size,
      height: size,
      src: imageUrl,
      googleImageParams: {
        width: size
      }
    });
  };
  const renderButton = () => {
    if (!isMobile) {
      const _a = ctaButton || {}, restCtaButton = __objRest(_a, ["onClick"]);
      return /* @__PURE__ */ React.createElement(React.Fragment, null, !!ctaButton && /* @__PURE__ */ React.createElement(Button, __spreadProps(__spreadValues(__spreadValues({
        disabled: quantityDesktop === 0
      }, restCtaButton), ctaTracking), {
        onClick: handleAddToCartDesktop,
        size: "m",
        prefix: /* @__PURE__ */ React.createElement(AddOutlined, {
          style: { marginRight: 12 },
          size: 16
        }),
        block: true
      })), !!contactButton && /* @__PURE__ */ React.createElement(Button, __spreadProps(__spreadValues(__spreadValues({}, contactButton), ctaTracking), {
        size: "m",
        block: true
      })));
    }
    if (!addToCart)
      return null;
    const { quantityInCart } = addToCart;
    if (quantityInCart === 0) {
      if (ctaButton)
        return /* @__PURE__ */ React.createElement(Button, __spreadProps(__spreadValues(__spreadValues({}, ctaButton), ctaTracking), {
          size: "s",
          block: true,
          padding: "s0"
        }));
      else
        return /* @__PURE__ */ React.createElement(Button, __spreadProps(__spreadValues(__spreadValues({}, contactButton), ctaTracking), {
          size: "s",
          block: true,
          padding: "s0"
        }));
    }
    if ((quantityInCart != null ? quantityInCart : 0) > 0) {
      return renderQuantity();
    }
  };
  return isMobile ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(WrapperRowMobile, __spreadValues({}, tracking), /* @__PURE__ */ React.createElement(CustomLink, __spreadValues({}, link), renderImage(96)), /* @__PURE__ */ React.createElement(ProductInfoMobile, null, productInfo, /* @__PURE__ */ React.createElement(SubTitle, {
    level: 1,
    color: "#b91d2a"
  }, lastestPrice), /* @__PURE__ */ React.createElement("div", {
    style: { marginTop: 16 }
  }, renderButton())))) : /* @__PURE__ */ React.createElement(WrapperRowDesktop, __spreadValues({
    align: "middle"
  }, tracking), /* @__PURE__ */ React.createElement(Col, {
    flex: "43%"
  }, /* @__PURE__ */ React.createElement(ProductInfoWrapper, null, /* @__PURE__ */ React.createElement(CustomLink, __spreadValues({}, link), renderImage(108)), /* @__PURE__ */ React.createElement(ProductInfo, null, productInfo))), /* @__PURE__ */ React.createElement(Col, {
    flex: "43%"
  }, /* @__PURE__ */ React.createElement(PaymentWrapper, null, /* @__PURE__ */ React.createElement(PurchasedPrice, null, /* @__PURE__ */ React.createElement(Body, {
    level: 1,
    color: "textSecondary"
  }, "Gi\xE1 b\xE1n"), /* @__PURE__ */ React.createElement(SubTitle, {
    level: 1,
    weight: "bold"
  }, lastestPrice)), /* @__PURE__ */ React.createElement(QuantityInfo, null, renderQuantity()), /* @__PURE__ */ React.createElement(TotalPrice, null, /* @__PURE__ */ React.createElement(Body, {
    level: 1,
    color: "textSecondary"
  }, "T\u1ED5ng ti\u1EC1n"), /* @__PURE__ */ React.createElement(Title, {
    level: 2,
    color: "#b91d2a"
  }, formattedTotalPrice)))), /* @__PURE__ */ React.createElement(Col, {
    flex: "14%"
  }, renderButton()));
};
const WrapperRowDesktop = styled(Row)`
  padding: 20px;
  background-color: #ffffff;
`;
const WrapperRowMobile = styled(Row)`
  padding: 18px;
  border-radius: 4px;
  background-color: #ffffff;
`;
const SkuWrapper = styled.div`
  display: flex;
  align-items: center;
`;
const ProductImage = styled(EnhancedImage)`
  border: 1px solid #e4e5f0;
  border-radius: 4px;
  min-width: 108px;
`;
const TotalPrice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
  margin-right: 12px;
`;
const PurchasedPrice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
`;
const ProductInfoWrapper = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;
const PaymentWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  gap: 24px;
  flex: 1 0 auto;
`;
const ProductInfo = styled.div`
  width: 100%;
`;
const QuantityInfo = styled.div`
  display: flex;
  gap: 40px;
  align-items: center;
`;
const Divider = styled.div`
  width: 8px;
  height: 0;
  transform: rotate(90deg);
  border: 1px solid #e4e5f0;
  margin: 0 8px;
`;
const ProductInfoMobile = styled.div`
  margin-left: 12px;
  flex: 1;
`;

export { ProductListItem };
